/**
 * Ten seconds, once, before the first note.
 *
 * ## Why there is a flow here at all
 *
 * The alternative shipped for months: eighteen sliders on `/dev/tuning` and a player who
 * has never heard the word "chroma". The only parameter a new player genuinely needs set
 * is the energy gate, and the only way to know it is to listen to their room — which
 * takes four seconds of them not playing. So this asks for exactly that, and then for one
 * chord, and then stops asking.
 *
 * ## Why the chord step cannot fail loudly
 *
 * The check chord is not a test the player can fail. If the recogniser hears something
 * else, that is a fact about the recogniser in this room, and the copy says so — the
 * escape is the tuning screen, not "try harder". Nothing is written unless C major is
 * actually heard, so every way out of this sheet at every step leaves the app exactly as
 * it was.
 *
 * ## Why it is handed the recogniser
 *
 * It needs two things the session view does not otherwise expose: the `diagnostics` event
 * stream (the room's level, ten times a second) and `setParams` (so the measured gate
 * takes effect on the next frame rather than the next launch). It takes them as a prop
 * rather than reaching for the host, which is what lets the whole flow be driven by a
 * scripted fake in a test — including the timings, which are props too.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  DEFAULT_RECOGNIZER_PARAMS,
  type ChordRecognizer,
  type KeyContext,
  type RecognizerEvent,
} from '@hc/shared';
import { BottomSheet } from '../system/BottomSheet.js';
import { ChordSymbol } from '../system/ChordSymbol.js';
import { hypothesisToChord } from '../system/chordReading.js';
import {
  assessCheckChord,
  capGateBelowSignal,
  diagnosticsFrames,
  gateFromNoiseFloor,
  loudestEnergyDb,
  measureNoiseFloor,
  saveRoomProfile,
  type CalibrationVerdict,
} from '../../audio/calibration.js';

/** Long enough for a room to show its noise, short enough that nobody walks away. */
export const QUIET_MS = 4000;
/** A generous window: finding C major on an unfamiliar keyboard is allowed to take a moment. */
export const LISTEN_MS = 8000;

const PROGRESS_TICK_MS = 100;

/**
 * The player was asked for C major, so C major is the context the reading is spelled in.
 * There is no key estimate yet — nothing has been played into the session.
 */
const CHECK_KEY: KeyContext = { tonicPc: 0, mode: 'major' };

type Step = 'quiet' | 'chord' | 'result';

export interface CalibrationSheetProps {
  open: boolean;
  /** Dismissal at any step. Nothing has been written unless the result said so. */
  onClose: () => void;
  /**
   * The live recogniser. `on` for the diagnostics stream, `setParams` to apply the gate —
   * deliberately the narrowest slice that does the job, so a fake is four lines.
   */
  recognizer: Pick<ChordRecognizer, 'on' | 'setParams'>;
  /** Overridden by tests, which have no patience. */
  quietMs?: number;
  listenMs?: number;
  onError?: (error: unknown, context: string) => void;
}

export function CalibrationSheet({
  open,
  onClose,
  recognizer,
  quietMs = QUIET_MS,
  listenMs = LISTEN_MS,
  onError,
}: CalibrationSheetProps): React.JSX.Element {
  const [step, setStep] = useState<Step>('quiet');
  const [progress, setProgress] = useState(0);
  const [verdict, setVerdict] = useState<CalibrationVerdict | null>(null);
  const [gateDb, setGateDb] = useState<number | null>(null);
  /**
   * The measured gate, mirrored out of state. The listening effect reads it when the
   * chord step ends, and putting it in that effect's dependencies would mean re-running —
   * and so re-subscribing — in the middle of listening.
   */
  const gateRef = useRef<number | null>(null);

  const restart = useCallback(() => {
    gateRef.current = null;
    setGateDb(null);
    setVerdict(null);
    setProgress(0);
    setStep('quiet');
  }, []);

  // Reopening is starting again, not resuming: a sheet that came back showing last time's
  // verdict would be reporting on a room the iPad may no longer be in.
  useEffect(() => {
    if (open) restart();
  }, [open, restart]);

  useEffect(() => {
    if (!open || step === 'result') return;

    const durationMs = step === 'quiet' ? quietMs : listenMs;
    const startedAt = Date.now();
    const events: RecognizerEvent[] = [];
    let done = false;
    let ticker = 0;
    let timer = 0;
    let off: (() => void) | null = null;

    const stop = (): void => {
      window.clearInterval(ticker);
      window.clearTimeout(timer);
      off?.();
      off = null;
    };

    const finish = (): void => {
      if (done) return;
      done = true;
      stop();

      if (step === 'quiet') {
        const { noiseFloorDb } = measureNoiseFloor(diagnosticsFrames(events));
        gateRef.current = gateFromNoiseFloor(noiseFloorDb);
        setProgress(0);
        setStep('chord');
        return;
      }

      const outcome = assessCheckChord(events);
      if (outcome.kind === 'confirmed') {
        // Capped against the chord that was just heard, because a loud room can propose a
        // gate above the playing it is meant to let through. See `capGateBelowSignal`.
        const measured = gateRef.current ?? DEFAULT_RECOGNIZER_PARAMS.energyGateDb;
        const gate = capGateBelowSignal(measured, loudestEnergyDb(diagnosticsFrames(events)));
        gateRef.current = gate;
        // Live first — it takes effect on the next frame and cannot fail. The write is
        // what makes it survive a reload, and a storage failure must not undo the setting
        // the player was just told about.
        recognizer.setParams({ energyGateDb: gate });
        void saveRoomProfile(gate).catch((error: unknown) => onError?.(error, 'calibration'));
      }
      setGateDb(gateRef.current);
      setVerdict(outcome);
      setStep('result');
    };

    ticker = window.setInterval(() => {
      setProgress(Math.min(1, (Date.now() - startedAt) / durationMs));
    }, PROGRESS_TICK_MS);
    timer = window.setTimeout(finish, durationMs);
    off = recognizer.on((event) => {
      events.push(event);
      // C major heard: end the wait. Standing there for the remaining seconds after the
      // player has done the thing asked of them is theatre.
      if (
        step === 'chord' &&
        event.type === 'chord' &&
        assessCheckChord([event]).kind === 'confirmed'
      ) {
        finish();
      }
    });

    return stop;
  }, [listenMs, onError, open, quietMs, recognizer, step]);

  const heardChord =
    verdict && verdict.kind === 'heard-other' ? hypothesisToChord(verdict.heard, CHECK_KEY) : null;

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title={titleFor(step, verdict)}
      eyebrow="setting up"
      testId="hc-calibration"
    >
      {step === 'quiet' ? (
        <div data-testid="hc-calibration-step" data-step="quiet">
          <p className="hc-sheet__quiet">
            Hands off the keys for a few seconds. I am listening to the room, so I know what silence
            sounds like here.
          </p>
          <Progress value={progress} label="Listening to the room" />
        </div>
      ) : null}

      {step === 'chord' ? (
        <div data-testid="hc-calibration-step" data-step="chord">
          <p className="hc-sheet__quiet">
            Any voicing, anywhere on the keyboard — C, E and G together.
          </p>
          <Progress value={progress} label="Waiting for a chord" />
        </div>
      ) : null}

      {step === 'result' && verdict ? (
        <div data-testid="hc-calibration-result" data-verdict={verdict.kind}>
          {verdict.kind === 'confirmed' ? (
            <p className="hc-sheet__quiet" data-testid="hc-calibration-gate">
              Heard it. Listening starts at {gateDb} dB, just above this room&rsquo;s own noise.
              Everything else is left at its default; the tuning screen is there if you ever want
              it.
            </p>
          ) : null}

          {verdict.kind === 'heard-other' && heardChord ? (
            <p className="hc-sheet__quiet">
              I heard <ChordSymbol symbol={heardChord.symbol} />, not C major. That is worth a look
              on the tuning screen rather than another attempt — nothing has been changed here.
            </p>
          ) : null}

          {verdict.kind === 'nothing' ? (
            <p className="hc-sheet__quiet">
              Nothing came through. Check that the iPad is near the piano and that nothing is
              covering its microphone. Nothing has been changed here.
            </p>
          ) : null}

          <div className="hc-sheet__actions">
            {verdict.kind === 'confirmed' ? (
              <button
                type="button"
                className="hc-button hc-button--primary"
                data-testid="hc-calibration-done"
                onClick={onClose}
              >
                Start playing
              </button>
            ) : (
              <>
                <button
                  type="button"
                  className="hc-button hc-button--quiet"
                  data-testid="hc-calibration-skip"
                  onClick={onClose}
                >
                  Skip — tune it by hand on the tuning screen later
                </button>
                <button
                  type="button"
                  className="hc-button hc-button--primary"
                  data-testid="hc-calibration-retry"
                  onClick={restart}
                >
                  Try again
                </button>
              </>
            )}
          </div>
        </div>
      ) : null}

      {step === 'result' ? null : (
        <div className="hc-sheet__actions">
          <button
            type="button"
            className="hc-button hc-button--quiet"
            data-testid="hc-calibration-skip"
            onClick={onClose}
          >
            Skip
          </button>
        </div>
      )}
    </BottomSheet>
  );
}

function titleFor(step: Step, verdict: CalibrationVerdict | null): string {
  if (step === 'quiet') return 'Stay quiet for a moment';
  if (step === 'chord') return 'Now play a C major chord';
  if (verdict?.kind === 'confirmed') return 'Set up for this room';
  if (verdict?.kind === 'heard-other') return 'That is not what I heard';
  return 'I did not hear a chord';
}

/**
 * The waiting bar.
 *
 * Styled inline rather than in `styles/surfaces.css`: this chunk does not own the
 * stylesheet, and two rules that exist only for a sheet seen once are not worth a
 * cross-chunk edit. The colours are tokens, so it follows the theme like everything else.
 */
function Progress({ value, label }: { value: number; label: string }): React.JSX.Element {
  return (
    <div
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(value * 100)}
      data-testid="hc-calibration-progress"
      style={{
        marginTop: '1rem',
        height: '0.35rem',
        borderRadius: '999px',
        background: 'var(--hc-line)',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          width: `${Math.round(value * 100)}%`,
          height: '100%',
          background: 'var(--hc-accent)',
          transition: 'width 120ms linear',
        }}
      />
    </div>
  );
}
